import React, { useState, useEffect, useRef } from 'react';
import {
    Typography,
    Card,
    Table,
    Button,
    Space,
    Input,
    Select,
    Modal,
    Form,
    DatePicker,
    Divider,
    InputNumber,
    message,
    Popconfirm,
    Tag,
    Tooltip,
    Row,
    Col,
    Statistic,
    Drawer,
    Descriptions,
    Tabs,
    Empty,
    List,
    FloatButton,
    Grid,
    Radio,
    Alert,
    Checkbox
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    FileTextOutlined,
    DollarOutlined,
    CalendarOutlined,
    UserOutlined,
    SwapOutlined,
    PrinterOutlined,
    FilterOutlined,
    CalculatorOutlined,
    TruckOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getQuotes, getQuoteById, createQuote, updateQuote, deleteQuote, convertQuoteToReceipt } from '../../services/quoteService';
import UnitsSelect from '../../components/UnitsSelect';
import { getContacts, getCustomersForDropdown } from '../../services/contactService';
import { getProducts } from '../../services/productService';
import { generateQuotePDF } from '../../services/quotePdfService';
import { getDeliveryNotes, getDeliveryNoteById, createDeliveryNote, updateDeliveryNote, deleteDeliveryNote, convertQuoteToDeliveryNote } from '../../services/deliveryNoteService';
import {
    getCompanySettings,
    updateCompanySettings,
    uploadCompanyLogo,
    deleteCompanyLogo,
    getLogoUrl
} from '../../services/settingsService';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const QuotesPage = () => {
    const [QuoteDetailInfo, setQuotesDetailInfo] = useState({});
    const [quotes, setQuotes] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [editingQuote, setEditingQuote] = useState(null);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [form] = Form.useForm();

    const [pdfModalVisible, setPdfModalVisible] = useState(false);
    const [pdfDataUrl, setPdfDataUrl] = useState('');
    const [pdfFileName, setPdfFileName] = useState('quotation.pdf');

    const [company, setCompany] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);

    // Responsive state
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);

    // Pagination and filters
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        contact_id: '',
    });
    const [statusCounts, setStatusCounts] = useState([]);

    // Status options and colors
    const statusOptions = [
        { value: 'draft', label: 'ร่าง', color: 'default' },
        { value: 'sent', label: 'ส่งแล้ว', color: 'processing' },
        { value: 'accepted', label: 'อนุมัติแล้ว', color: 'success' },
        { value: 'rejected', label: 'ปฏิเสธ', color: 'error' },
        { value: 'expired', label: 'หมดอายุ', color: 'warning' },
    ];

    const getStatusColor = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.color || 'default';
    };

    const getStatusLabel = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.label || status;
    };

    const handleDownloadPdf = () => {
        if (!pdfDataUrl) return;

        const link = document.createElement('a');
        link.href = pdfDataUrl;
        link.download = pdfFileName || 'quotation.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Custom filter function for contact selection
    const customContactFilter = (input, option) => {
        if (!input) return true;

        // Handle case where option.children might be React element or string
        let searchText = '';

        if (typeof option.children === 'string') {
            searchText = option.children;
        } else if (React.isValidElement(option.children)) {
            // If it's a React element, try to get text content
            searchText = option.children.props?.children || '';
        } else if (Array.isArray(option.children)) {
            // If it's an array, join text content
            searchText = option.children.map(child =>
                typeof child === 'string' ? child : ''
            ).join(' ');
        }

        // Also check the label prop if available
        if (option.label && typeof option.label === 'string') {
            searchText += ' ' + option.label;
        }

        return searchText.toLowerCase().includes(input.toLowerCase());
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await getCompanySettings();
            //console.log('company data:', response.data.data);
            if (response.data.success) {
                const data = response.data.data;
                setCompany(data);
                form.setFieldsValue(data);
                setLogoUrl(data.logo_path ? getLogoUrl(`${data.logo_path}`) : null);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            message.error('ไม่สามารถโหลดข้อมูลบริษัทได้');
        } finally {
            setLoading(false);
            //console.log('image url:', logoUrl);
        }
    };

    // Load data
    useEffect(() => {
        loadQuotes();
        loadContacts();
        loadProducts();
    }, [pagination.current, pagination.pageSize, filters]);

    const loadQuotes = async () => {
        try {
            setLoading(true);
            const response = await getQuotes(
                pagination.current,
                pagination.pageSize,
                filters.search,
                filters.status,
                filters.contact_id
            );

            if (response.success) {
                setQuotes(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.pagination.total,
                }));
                setStatusCounts(response.statusCounts || []);
            }
        } catch (error) {
            //message.error('เกิดข้อผิดพลาดในการโหลดข้อมูลใบเสนอราคา');
            console.error('Error loading quotes:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadContacts = async () => {
        try {
            const response = await getCustomersForDropdown();
            console.log('contacts:', response);
            if (response.success) {
                setContacts(response.data);
            }
        } catch (error) {
            console.error('Error loading contacts:', error);
        }
    };

    const loadProducts = async () => {
        try {
            const response = await getProducts(1, 1000); // Get all products
            if (response.success) {
                setProducts(response.data);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        }
    };

    // Handle table changes
    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    // Handle search
    const handleSearch = (value) => {
        setFilters(prev => ({ ...prev, search: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({ search: '', status: '', contact_id: '' });
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Handle modal operations
    const showCreateModal = () => {
        setEditingQuote(null);
        setModalVisible(true);
        form.resetFields();
        // Set default values
        form.setFieldsValue({
            quote_date: dayjs(),
            valid_until: dayjs().add(30, 'day'),
            tax_rate: 7,
            discount: 0,
            vat_mode: 'include',
            items: [{ description: '', quantity: 1, unit_price: 0, unit: 'ชิ้น' }]
        });
    };

    const showEditModal = async (quote) => {
        try {
            const response = await getQuoteById(quote.id);
            console.log('ข้อมูลใบเสนอราคา', response.data)
            if (response.success) {
                setEditingQuote(response.data);
                setQuotesDetailInfo(response.data)
                setModalVisible(true);
                form.setFieldsValue({
                    contact_id: response.data.contact_id,
                    quote_date: dayjs(response.data.quote_date),
                    valid_until: response.data.valid_until ? dayjs(response.data.valid_until) : null,
                    tax_rate: response.data.tax_rate,
                    discount: response.data.discount,
                    vat_mode: response.data.vat_mode || 'include',
                    total_amount: response.data.total_amount,
                    tax_amount: response.data.tax_amount,
                    subtotal: response.data.subtotal,
                    notes: response.data.notes,
                    items: response.data.items?.map(item => ({
                        ...item,
                        unit: item.unit || 'ชิ้น'
                    })) || [{ description: '', quantity: 1, unit_price: 0, unit: 'ชิ้น' }]
                });
            }
        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการโหลดข้อมูลใบเสนอราคา');
        }
    };

    const showDetailDrawer = async (quote) => {
        try {
            setLoading(true);
            const response = await getQuoteById(quote.id);
            console.log('selected', response.data);
            if (response.success) {
                setSelectedQuote(response.data);
                setDetailDrawerVisible(true);
            }
        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการโหลดรายละเอียดใบเสนอราคา');
        } finally {
            setLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = async (values) => {
        try {

            console.log('values', values);

            setLoading(true);

            const formData = {
                ...values,
                quote_date: values.quote_date.format('YYYY-MM-DD'),
                valid_until: values.valid_until ? values.valid_until.format('YYYY-MM-DD') : null,
                vat_mode: values.vat_mode || 'include',
            };

            if (editingQuote) {
                await updateQuote(editingQuote.id, formData);
                message.success('อัปเดตใบเสนอราคาเรียบร้อยแล้ว');
            } else {
                await createQuote(formData);
                message.success('สร้างใบเสนอราคาเรียบร้อยแล้ว');
            }

            setModalVisible(false);
            loadQuotes();
        } catch (error) {
            message.error(error.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
        } finally {
            setLoading(false);
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            await deleteQuote(id);
            message.success('ลบใบเสนอราคาเรียบร้อยแล้ว');
            loadQuotes();
        } catch (error) {
            message.error(error.message || 'เกิดข้อผิดพลาดในการลบข้อมูล');
        }
    };

    // Handle convert to receipt
    const handleConvertToReceipt = async (quote) => {
        try {
            const response = await convertQuoteToReceipt(quote.id);
            if (response.success) {
                message.success(`แปลงเป็นใบแจ้งหนี้เรียบร้อยแล้ว (เลขที่: ${response.data.invoice_number})`);
                loadQuotes();
            }
        } catch (error) {
            message.error(error.message || 'เกิดข้อผิดพลาดในการแปลงเป็นใบแจ้งหนี้');
        }
    };

    // Handle status update
    const handleStatusUpdate = async (quote, newStatus) => {
        try {
            await updateQuote(quote.id, { status: newStatus });
            message.success('อัปเดตสถานะเรียบร้อยแล้ว');
            loadQuotes();
            if (selectedQuote && selectedQuote.id === quote.id) {
                setSelectedQuote(prev => ({ ...prev, status: newStatus }));
            }
        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการอัปเดตสถานะ');
        }
    };

    // Print quote function using pdfMake
    const handlePrintQuote = async (record) => {
        try {

            message.loading('กำลังสร้าง PDF...', 1);

            const response = await getQuoteById(record.id);
            console.log('Printing...', response.data);

            if (response.success) {
                const quoteData = response.data;

                // แปลงข้อมูลให้เข้ากับ format ของ PDF service
                const formattedQuote = {
                    quoteNumber: quoteData.quote_number,
                    quoteDate: quoteData.quote_date,
                    validUntil: quoteData.valid_until,
                    customer: {
                        name: quoteData.contact_name,
                        company: quoteData.company_name || '',
                        address: quoteData.billing_address || '',
                        phone: quoteData.phone || '',
                        email: quoteData.email || '',
                        tax_id: quoteData.tax_id || ''
                    },
                    items: quoteData.items?.map(item => ({
                        barcode: item.barcode,
                        name: item.product_name,
                        description: item.description,
                        quantity: item.quantity,
                        unit: item.unit || 'ชิ้น',
                        unitPrice: item.unit_price,
                        is_free: item.is_free
                    })) || [],
                    notes: quoteData.notes,
                    data: quoteData,
                    paymentTerms: 'ชำระเงินภายใน 30 วัน หลังจากได้รับใบแจ้งหนี้'
                };


                // สร้าง PDF เป็น dataUrl
                const pdfResult = await generateQuotePDF(formattedQuote, company, 'include');
                setPdfDataUrl(pdfResult.dataUrl);
                setPdfFileName(pdfResult.fileName);
                setPdfModalVisible(true);
                message.success('สร้าง PDF เรียบร้อยแล้ว');

            } else {
                //message.error('ไม่สามารถโหลดข้อมูลใบเสนอราคาได้');
            }
        } catch (error) {
            console.error('PDF generation error:', error);
            message.error('เกิดข้อผิดพลาดในการสร้าง PDF: ' + error.message);
        }
    };


    // เพิ่มฟังก์ชัน
    const handleConvertToDeliveryNote = async (record) => {
        Modal.confirm({
            title: 'แปลงเป็นใบส่งของชั่วคราว',
            content: `คุณต้องการแปลงใบเสนอราคา ${record.quote_number} เป็นใบส่งของชั่วคราวใช่หรือไม่?`,
            okText: 'ยืนยัน',
            cancelText: 'ยกเลิก',
            onOk: async () => {
                try {
                    message.loading('กำลังแปลง...', 0);

                    const data = {
                        issue_date: new Date().toISOString().split('T')[0],
                        due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        status: 'draft',
                        notes: `แปลงจากใบเสนอราคา ${record.quote_number}`
                    };

                    const response = await convertQuoteToDeliveryNote(record.id, data);

                    //message.destroy();

                    if (response.success) {
                        message.success(`แปลงเป็นใบส่งของชั่วคราว ${response.data.delivery_note_number} เรียบร้อยแล้ว`);

                        // ถามว่าต้องการไปหน้าใบส่งของหรือไม่
                        Modal.confirm({
                            title: 'สำเร็จ',
                            content: `สร้างใบส่งของชั่วคราว ${response.data.delivery_note_number} เรียบร้อยแล้ว ต้องการไปหน้าใบส่งของหรือไม่?`,
                            okText: 'ไปหน้าใบส่งของ',
                            cancelText: 'อยู่หน้านี้',
                            onOk: () => {
                                window.location.href = '/delivery-notes'; // ปรับ path ตาม routing ของคุณ
                            }
                        });
                    } else {
                        message.error(response.message || 'ไม่สามารถแปลงได้');
                    }
                } catch (error) {
                    //message.destroy();
                    console.error('Convert error:', error);
                    message.error('เกิดข้อผิดพลาด: ' + (error.response?.data?.message || error.message));
                }
            }
        });
    };


    // Mobile Quote Card Component
    const QuoteCard = ({ quote }) => (
        <Card
            size="small"
            style={{ marginBottom: 16, padding: '0px' }}
            actions={[
                <Tooltip title="ดูรายละเอียด">
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => showDetailDrawer(quote)}
                        size="small"
                    />
                </Tooltip>,
                <Tooltip title="พิมพ์ PDF">
                    <Button
                        type="text"
                        icon={<PrinterOutlined />}
                        onClick={() => handlePrintQuote(quote)}
                        size="small"
                        style={{ color: '#722ed1' }}
                    />
                </Tooltip>,
                <Tooltip title="แปลงเป็นใบส่งของ">
                    <Button
                        type="link"
                        icon={<TruckOutlined />}
                        onClick={() => handleConvertToDeliveryNote(quote)}
                        style={{ color: '#52c41a' }}
                    />
                </Tooltip>,
                <Tooltip title="แก้ไข">
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => showEditModal(quote)}
                        size="small"
                    />
                </Tooltip>,
                quote.status === 'accepted' ? (
                    <Tooltip title="แปลงเป็นใบแจ้งหนี้">
                        <Button
                            type="text"
                            icon={<SwapOutlined />}
                            onClick={() => handleConvertToReceipt(quote)}
                            size="small"
                            style={{ color: '#52c41a' }}
                        />
                    </Tooltip>
                ) : (
                    <Popconfirm
                        title="คุณแน่ใจหรือไม่ที่จะลบใบเสนอราคานี้?"
                        onConfirm={() => handleDelete(quote.id)}
                        okText="ใช่"
                        cancelText="ไม่"
                    >
                        <Tooltip title="ลบ">
                            <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                )
            ]}
        >
            <div onClick={() => showDetailDrawer(quote)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong>{quote.quote_number}</strong>
                    <Tag color={getStatusColor(quote.status)}>
                        {getStatusLabel(quote.status)}
                    </Tag>
                </div>

                <div style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{quote.contact_name}</div>
                    {quote.company_name && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{quote.company_name}</div>
                    )}
                </div>

                <Row gutter={16} style={{ fontSize: '12px', color: '#666' }}>
                    <Col span={12}>
                        <CalendarOutlined style={{ marginRight: 4 }} />
                        {dayjs(quote.quote_date).format('DD/MM/YYYY')}
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <DollarOutlined style={{ marginRight: 4 }} />
                        ฿{parseFloat(quote.total_amount)}
                    </Col>
                </Row>

                {quote.valid_until && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        หมดอายุ: {dayjs(quote.valid_until).format('DD/MM/YYYY')}
                    </div>
                )}
            </div>
        </Card>
    );

    const totalAmount = quotes.reduce((sum, item) => sum + parseFloat(item.total_amount), 0);
    const totalTaxAmount = quotes.reduce((sum, item) => sum + parseFloat(item.tax_amount), 0);

    // Table columns
    const columns = [
        {
            title: 'เลขที่ใบเสนอราคา',
            dataIndex: 'quote_number',
            key: 'quote_number',
            width: 150,
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => showDetailDrawer(record)}
                    style={{ padding: 0, height: 'auto' }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: 'ลูกค้า',
            key: 'customer',
            width: 200,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{record.contact_name}</div>
                    {record.company_name && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{record.company_name}</div>
                    )}
                </div>
            ),
        },
        {
            title: 'วันที่เสนอราคา',
            dataIndex: 'quote_date',
            key: 'quote_date',
            width: 120,
            render: (date) => dayjs(date).format('DD/MM/YYYY'),
        },
        {
            title: 'วันหมดอายุ',
            dataIndex: 'valid_until',
            key: 'valid_until',
            width: 120,
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'จำนวนเงิน',
            dataIndex: 'total_amount',
            key: 'total_amount',
            width: 120,
            align: 'right',
            render: (amount) => `฿${parseFloat(amount)}`,
        },
        {
            title: 'ภาษี',
            dataIndex: 'tax_amount',
            key: 'tax_amount',
            width: 120,
            align: 'right',
            render: (amount) => `฿${parseFloat(amount)}`,
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusLabel(status)}
                </Tag>
            ),
        },
        {
            title: 'การดำเนินการ',
            key: 'actions',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="ดูรายละเอียด">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => showDetailDrawer(record)}
                            size="small"
                        />
                    </Tooltip>

                    <Tooltip title="พิมพ์ PDF">
                        <Button
                            type="text"
                            icon={<PrinterOutlined />}
                            onClick={() => handlePrintQuote(record)}
                            size="small"
                            style={{ color: '#722ed1' }}
                        />
                    </Tooltip>

                    <Tooltip title="แก้ไข">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => showEditModal(record)}
                            size="small"
                        />
                    </Tooltip>

                    {record.status === 'accepted' && (
                        <Tooltip title="แปลงเป็นใบส่งของชั่วคราว">
                            <Button
                                type="text"
                                icon={<TruckOutlined />}
                                onClick={() => handleConvertToDeliveryNote(record)}
                                size="small"
                                style={{ color: '#52c41a' }}
                            />
                        </Tooltip>
                    )}


                    {record.status === 'accepted' && (
                        <Tooltip title="แปลงเป็นใบแจ้งหนี้">
                            <Button
                                type="text"
                                icon={<SwapOutlined />}
                                onClick={() => handleConvertToReceipt(record)}
                                size="small"
                                style={{ color: '#52c41a' }}
                            />
                        </Tooltip>
                    )}

                    <Popconfirm
                        title="คุณแน่ใจหรือไม่ที่จะลบใบเสนอราคานี้?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="ใช่"
                        cancelText="ไม่"
                    >
                        <Tooltip title="ลบ">
                            <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? '0px' : '24px' }}>
            <div style={{ marginBottom: '24px' }}>
                <Title level={2}>
                    <FileTextOutlined style={{ marginRight: '8px' }} />
                    จัดการใบเสนอราคา
                </Title>
            </div>

            {/* Modal แสดง PDF */}
            <Modal
                title="ใบเสนอราคา"
                visible={pdfModalVisible}
                onCancel={() => setPdfModalVisible(false)}
                footer={[
                    <Button key="download" type="primary" onClick={handleDownloadPdf}>
                        ดาวน์โหลด PDF
                    </Button>
                ]}
                width={isMobile ? '100%' : 800}
                bodyStyle={{ height: isMobile ? '80vh' : '600px' }}
            >
                {pdfDataUrl && (
                    <iframe
                        src={pdfDataUrl}
                        style={{ width: '100%', height: '100%' }}
                        frameBorder="0"
                        title="ตัวอย่างเอกสาร"
                    />
                )}
            </Modal>

            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                {statusCounts.map(item => (
                    <Col xs={12} sm={8} md={6} lg={4} key={item.status}>
                        <Card size={isMobile ? "small" : "default"}>
                            <Statistic
                                title={getStatusLabel(item.status)}
                                value={item.count}
                                valueStyle={{
                                    fontSize: isMobile ? '20px' : '24px',
                                    color: getStatusColor(item.status) === 'success' ? '#3f8600' :
                                        getStatusColor(item.status) === 'error' ? '#cf1322' :
                                            getStatusColor(item.status) === 'warning' ? '#d48806' : '#1890ff'
                                }}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Desktop Filters and Search */}
            {!isMobile && (
                <Card style={{ marginBottom: '24px' }}>
                    <Row gutter={16} align="middle">
                        <Col flex="300px">
                            <Input
                                placeholder="ค้นหาเลขที่ใบเสนอราคา หรือ ชื่อลูกค้า"
                                prefix={<SearchOutlined />}
                                value={filters.search}
                                onChange={(e) => handleSearch(e.target.value)}
                                allowClear
                            />
                        </Col>
                        <Col flex="200px">
                            <Select
                                placeholder="เลือกสถานะ"
                                value={filters.status}
                                onChange={(value) => handleFilterChange('status', value)}
                                allowClear
                                style={{ width: '100%' }}
                            >
                                {statusOptions.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col flex="200px">
                            <Select
                                placeholder="เลือกลูกค้า"
                                value={filters.contact_id}
                                onChange={(value) => handleFilterChange('contact_id', value)}
                                allowClear
                                showSearch
                                filterOption={customContactFilter}
                                style={{ width: '100%' }}
                            >
                                {contacts.map(contact => (
                                    <Option
                                        key={contact.id}
                                        value={contact.id}
                                        label={`${contact.name}${contact.company_name ? ` (${contact.company_name})` : ''}`}
                                    >
                                        {contact.name} {contact.company_name && `(${contact.company_name})`}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col>
                            <Space>
                                <Button onClick={clearFilters}>ล้างตัวกรอง</Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={showCreateModal}
                                >
                                    สร้างใบเสนอราคา
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            )}

            {/* Mobile Search and Filters */}
            {isMobile && (
                <>
                    <Card style={{ marginBottom: '16px', padding: '0px' }}>
                        <Input
                            placeholder="ค้นหาเลขที่ใบเสนอราคา หรือ ชื่อลูกค้า"
                            prefix={<SearchOutlined />}
                            value={filters.search}
                            onChange={(e) => handleSearch(e.target.value)}
                            allowClear
                        />
                        <div style={{ marginTop: '12px', textAlign: 'center' }}>
                            <Button
                                icon={<FilterOutlined />}
                                onClick={() => setMobileFiltersVisible(true)}
                                style={{ marginRight: '8px' }}
                            >
                                ตัวกรอง
                            </Button>
                        </div>
                    </Card>

                    {/* Mobile Filters Drawer */}
                    <Drawer
                        title="ตัวกรองข้อมูล"
                        placement="bottom"
                        height="auto"
                        open={mobileFiltersVisible}
                        onClose={() => setMobileFiltersVisible(false)}
                    >
                        <div style={{ padding: '16px 0' }}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>สถานะ</label>
                                <Select
                                    placeholder="เลือกสถานะ"
                                    value={filters.status}
                                    onChange={(value) => handleFilterChange('status', value)}
                                    allowClear
                                    style={{ width: '100%' }}
                                >
                                    {statusOptions.map(option => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>ลูกค้า</label>
                                <Select
                                    placeholder="เลือกลูกค้า"
                                    value={filters.contact_id}
                                    onChange={(value) => handleFilterChange('contact_id', value)}
                                    allowClear
                                    showSearch
                                    filterOption={customContactFilter}
                                    style={{ width: '100%' }}
                                >
                                    {contacts.map(contact => (
                                        <Option key={contact.id} value={contact.id}>
                                            {contact.name} {contact.company_name && `(${contact.company_name})`}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div style={{ textAlign: 'center', paddingTop: '16px' }}>
                                <Space>
                                    <Button onClick={clearFilters}>ล้างตัวกรอง</Button>
                                    <Button
                                        type="primary"
                                        onClick={() => setMobileFiltersVisible(false)}
                                    >
                                        ตกลง
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    </Drawer>
                </>
            )}

            {/* Data Display */}
            <Card style={{ padding: isMobile ? '0px' : '5px' }}>
                {isMobile ? (
                    /* Mobile: List View */
                    <List
                        loading={loading}
                        dataSource={quotes}
                        renderItem={(quote) => <QuoteCard key={quote.id} quote={quote} />}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            onChange: (page, pageSize) => {
                                setPagination(prev => ({ ...prev, current: page, pageSize }));
                            },
                            showSizeChanger: false,
                            showQuickJumper: false,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} จาก ${total} รายการ`,
                            size: 'small'
                        }}
                    />
                ) : (
                    /* Desktop: Table View */
                    <Table
                        columns={columns}
                        dataSource={quotes}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} จาก ${total} รายการ`,
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 1200 }}
                        summary={() => (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={3}>
                                        <strong></strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                        <strong></strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                        <strong></strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                        <strong></strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4}>
                                        <strong>{`รวม  ${totalAmount.toLocaleString()}`}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5}>
                                        <strong>{`ภาษี  ${totalTaxAmount.toLocaleString()}`}</strong>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                )}
            </Card>

            {/* Float Button for Mobile Add */}
            {isMobile && (
                <FloatButton
                    icon={<PlusOutlined />}
                    type="primary"
                    onClick={showCreateModal}
                    style={{ right: 24, bottom: 24 }}
                />
            )}

            {/* Create/Edit Form */}
            {isMobile ? (
                /* Mobile: Drawer */
                <Drawer
                    maskClosable={false}
                    title={editingQuote ? 'แก้ไขใบเสนอราคา' : 'สร้างใบเสนอราคาใหม่'}
                    open={modalVisible}
                    onClose={() => setModalVisible(false)}
                    width="100%"
                    placement="right"
                    destroyOnClose
                    extra={
                        <Space>
                            <Button onClick={() => setModalVisible(false)}>ยกเลิก</Button>
                            <Button
                                type="primary"
                                onClick={() => form.submit()}
                                loading={loading}
                            >
                                บันทึก
                            </Button>
                        </Space>
                    }
                >

                    <QuoteForm
                        form={form}
                        onFinish={handleSubmit}
                        contacts={contacts}
                        products={products}
                        loading={loading}
                        onCancel={() => setModalVisible(false)}
                        isMobile={isMobile}
                        QuoteDetailInfo={QuoteDetailInfo}
                    />

                </Drawer>
            ) : (
                /* Desktop: Modal */
                <Modal
                    maskClosable={false}
                    title={editingQuote ? 'แก้ไขใบเสนอราคา' : 'สร้างใบเสนอราคาใหม่'}
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    footer={null}
                    width={900}
                    destroyOnClose
                >
                    <QuoteForm
                        form={form}
                        onFinish={handleSubmit}
                        contacts={contacts}
                        products={products}
                        loading={loading}
                        onCancel={() => setModalVisible(false)}
                        isMobile={isMobile}
                        QuoteDetailInfo={QuoteDetailInfo}
                    />
                </Modal>
            )}

            {/* Detail Drawer */}
            <Drawer
                title="รายละเอียดใบเสนอราคา"
                open={detailDrawerVisible}
                onClose={() => setDetailDrawerVisible(false)}
                width={isMobile ? "100%" : 600}
                extra={
                    selectedQuote && (
                        <Space>
                            {selectedQuote.status !== 'accepted' && (
                                <Select
                                    value={selectedQuote.status}
                                    onChange={(value) => handleStatusUpdate(selectedQuote, value)}
                                    style={{ width: 120 }}
                                >
                                    {statusOptions.map(option => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                            {selectedQuote.status === 'accepted' && (
                                <Button
                                    type="primary"
                                    icon={<SwapOutlined />}
                                    onClick={() => handleConvertToReceipt(selectedQuote)}
                                >
                                    แปลงเป็นใบแจ้งหนี้
                                </Button>
                            )}
                        </Space>
                    )
                }
            >
                {selectedQuote && <QuoteDetail quote={selectedQuote} isMobile={isMobile} />}
            </Drawer>

        </div>
    );
};

// Quote Form Component พร้อม VAT Mode
const QuoteForm = ({ form, onFinish, contacts, products, loading, onCancel, isMobile, QuoteDetailInfo }) => {
    // State สำหรับ VAT mode และการคำนวณ
    const [vatMode, setVatMode] = useState('include');
    const [calcValues, setCalcValues] = useState({
        subtotal: 0,
        discount: 0,
        taxAmount: 0,
        totalAmount: 0
    });

    // หน่วยนับมาตรฐาน - ตอนนี้ใช้ API แทน hardcoded array
    // หน่วยนับจะถูกโหลดจาก /api/units ผ่าน UnitsSelect component

    console.log('QuoteDetail', QuoteDetailInfo);

    const getDetail = () => {
        setCalcValues({
            subtotal: QuoteDetailInfo.subtotal,
            discount: QuoteDetailInfo.discount,
            taxAmount: QuoteDetailInfo.tax_amount,
            totalAmount: QuoteDetailInfo.total_amount
        })
    }

    useEffect(() => {
        getDetail();
    }, [QuoteDetail]);


    // Custom filter function for contact selection
    const customContactFilter = (input, option) => {
        if (!input) return true;

        let searchText = '';

        if (typeof option.children === 'string') {
            searchText = option.children;
        } else if (React.isValidElement(option.children)) {
            searchText = option.children.props?.children || '';
        } else if (Array.isArray(option.children)) {
            searchText = option.children.map(child =>
                typeof child === 'string' ? child : ''
            ).join(' ');
        }

        return searchText.toLowerCase().includes(input.toLowerCase());
    };

    // ฟังก์ชันคำนวณยอดรวมตาม VAT mode
    // เสถียร
    // const calculateTotals = (items, discount = 0, taxRate = 7, vatMode = 'include') => {

    //     //แบบไม่มีระบบของแถม
    //     const itemsSubtotal = items.reduce((sum, item) => {
    //         return sum + ((item?.quantity || 0) * (item?.unit_price || 0));
    //     }, 0);

    //      let subtotal, taxAmount, totalAmount;

    //     if (vatMode === 'include') {
    //         // VAT INCLUDE: ราคารวมภาษีแล้ว
    //         const totalBeforeDiscount = itemsSubtotal;
    //         const discountAmount = discount || 0;
    //         totalAmount = totalBeforeDiscount - discountAmount;

    //         // คำนวณ subtotal และ tax จากยอดรวม
    //         subtotal = totalAmount / (1 + (taxRate / 100));
    //         taxAmount = totalAmount - subtotal;
    //     } else {
    //         // VAT EXCLUDE: ราคายังไม่รวมภาษี
    //         subtotal = itemsSubtotal;
    //         const discountAmount = discount || 0;
    //         const taxableAmount = subtotal - discountAmount;
    //         taxAmount = (taxableAmount * (taxRate || 0)) / 100;
    //         totalAmount = taxableAmount + taxAmount;
    //     }

    //     return {
    //         subtotal: Math.round(subtotal * 100) / 100,
    //         taxAmount: Math.round(taxAmount * 100) / 100,
    //         totalAmount: Math.round(totalAmount * 100) / 100,
    //         discountAmount: discount || 0
    //     };
    // };

    // ฟังก์ชันคำนวณยอดรวมตาม VAT mode
    const calculateTotals = (items, discount = 0, taxRate = 7, vatMode = 'include') => {

        //console.log('items editting...', items);

        // ✅ คำนวณเฉพาะรายการที่ไม่ใช่ของแถม (is_free !== true)
        const itemsSubtotal = items.reduce((sum, item) => {
            if (item?.is_free === true || item?.is_free === 1) {
                return sum; // ไม่นับรายการของแถม
            }
            return sum + ((item?.quantity || 0) * (item?.unit_price || 0));
        }, 0);

        let subtotal, taxAmount, totalAmount;

        if (vatMode === 'include') {
            // VAT INCLUDE: ราคารวมภาษีแล้ว
            const totalBeforeDiscount = itemsSubtotal;
            const discountAmount = discount || 0;
            totalAmount = totalBeforeDiscount - discountAmount;

            // คำนวณ subtotal และ tax จากยอดรวม
            subtotal = totalAmount / (1 + (taxRate / 100));
            taxAmount = totalAmount - subtotal;
        } else if (vatMode === 'exclude') {
            // VAT EXCLUDE: ราคายังไม่รวมภาษี
            subtotal = itemsSubtotal;
            const discountAmount = discount || 0;
            const taxableAmount = subtotal - discountAmount;
            taxAmount = (taxableAmount * (taxRate || 0)) / 100;
            totalAmount = taxableAmount + taxAmount;

        } else {
            subtotal = itemsSubtotal;
            const totalBeforeDiscount = itemsSubtotal;
            const discountAmount = discount || 0;
            taxAmount = 0;
            totalAmount = totalBeforeDiscount - discountAmount;
        }

        return {
            subtotal: Math.round(subtotal * 100) / 100,
            taxAmount: Math.round(taxAmount * 100) / 100,
            totalAmount: Math.round(totalAmount * 100) / 100,
            discountAmount: discount || 0
        };
    };


    // Watch form changes for real-time calculation
    const handleFormChange = () => {

        const items = form.getFieldValue('items') || [];
        const discount = form.getFieldValue('discount') || 0;
        const taxRate = form.getFieldValue('tax_rate') || 7;
        const currentVatMode = form.getFieldValue('vat_mode') || 'include';

        console.log('items handleFormChange', items);

        setVatMode(currentVatMode);
        const calculations = calculateTotals(items, discount, taxRate, currentVatMode);
        setCalcValues(calculations);

    };

    // Enhanced form submission with calculation
    const handleFinish = (values) => {

        const calculations = calculateTotals(
            values.items || [],
            values.discount || 0,
            values.tax_rate || 7,
            values.vat_mode || 'include'
        );

        const enhancedValues = {
            ...values,
            subtotal: calcValues.subtotal,
            tax_amount: calcValues.taxAmount,
            total_amount: calcValues.totalAmount
        };

        onFinish(enhancedValues);
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            onValuesChange={handleFormChange}
            initialValues={{
                quote_date: dayjs(),
                valid_until: dayjs().add(30, 'day'),
                tax_rate: 7,
                discount: 0,
                vat_mode: 'include',
                items: [{ description: '', quantity: 1, unit_price: 0, unit: 'กล่อง' }]
            }}
        >
            <Row gutter={isMobile ? [0, 16] : 16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="contact_id"
                        label="ลูกค้า"
                        rules={[{ required: true, message: 'กรุณาเลือกลูกค้า' }]}
                    >
                        <Select
                            placeholder="เลือกลูกค้า"
                            showSearch
                            filterOption={customContactFilter}
                        >
                            {contacts.map(contact => (
                                <Option key={contact.id} value={contact.id}>
                                    {contact.name} {contact.company_name && `(${contact.company_name})`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                    <Form.Item
                        name="quote_date"
                        label="วันที่เสนอราคา"
                        rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                    <Form.Item
                        name="valid_until"
                        label="วันหมดอายุ"
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>

            <Divider>รายการสินค้า</Divider>

            <Form.List name="items">
                {(fields, { add, remove }) => (
                    <>
                        {fields.map((field, index) => (
                            <Card
                                key={field.key}
                                size="small"
                                title={`รายการที่ ${index + 1}`}
                                extra={
                                    fields.length > 1 && (
                                        <Button
                                            type="link"
                                            danger
                                            onClick={() => remove(field.name)}
                                        >
                                            ลบ
                                        </Button>
                                    )
                                }
                                style={{ marginBottom: 16 }}
                            >
                                <Row gutter={isMobile ? [0, 16] : 16}>
                                    <Col xs={24} sm={8}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'product_id']}
                                            label="สินค้า"
                                        >
                                            <Select
                                                placeholder="เลือกสินค้า (ไม่บังคับ)"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) => {
                                                    if (!input) return true;
                                                    const searchText = typeof option.children === 'string'
                                                        ? option.children
                                                        : '';
                                                    return searchText.toLowerCase().includes(input.toLowerCase());
                                                }}
                                                onChange={(value, option) => {
                                                    if (value && option) {
                                                        const currentItems = form.getFieldValue('items');
                                                        currentItems[index] = {
                                                            ...currentItems[index],
                                                            product_id: value,
                                                            // description: option.children,
                                                            unit: option.unit,
                                                            unit_price: products.find(p => p.id === value)?.price || 0
                                                        };
                                                        form.setFieldsValue({ items: currentItems });
                                                        handleFormChange();
                                                    }
                                                }}
                                            >
                                                {products.map(product => (
                                                    <Option key={product.id} value={product.id}>
                                                        {`${product.barcode}-${product.name}`}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'is_free']}
                                            valuePropName="checked"
                                            initialValue={false}
                                        >
                                            <div style={{ paddingTop: isMobile ? 0 : 30 }}>
                                                <Checkbox
                                                    danger
                                                    onChange={(e) => {
                                                        const currentItems = form.getFieldValue('items');
                                                        currentItems[index].is_free = e.target.checked;
                                                        form.setFieldsValue({ items: currentItems });
                                                        handleFormChange();
                                                    }}
                                                    checked={form.getFieldValue('items')?.[index]?.is_free || false}
                                                >
                                                    <span style={{ color: 'red' }}>รายการแถมฟรี !</span>
                                                </Checkbox>
                                            </div>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'description']}
                                            label="รายละเอียด"
                                            rules={[{ required: false, message: 'กรุณากรอกรายละเอียด' }]}
                                        >
                                            <Input placeholder="รายละเอียดสินค้า/บริการ" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={isMobile ? [0, 16] : 16}>
                                    <Col xs={12} sm={6}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'quantity']}
                                            label="จำนวน"
                                            rules={[{ required: true, message: 'กรุณากรอกจำนวน' }]}
                                        >
                                            <InputNumber
                                                min={0.01}
                                                step={0.01}
                                                style={{ width: '100%' }}
                                                placeholder="จำนวน"
                                                onChange={handleFormChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'unit']}
                                            label="หน่วยนับ"
                                            rules={[{ required: true, message: 'กรุณาเลือกหน่วยนับ' }]}
                                        >
                                            <UnitsSelect
                                                placeholder="เลือกหน่วยนับ"
                                                showSearch
                                                allowClear
                                                showRefresh
                                                valueField="unit_name"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'unit_price']}
                                            label="ราคาต่อหน่วย"
                                            rules={[{ required: true, message: 'กรุณากรอกราคา' }]}
                                        >
                                            <InputNumber
                                                min={0}
                                                step={0.01}
                                                style={{ width: '100%' }}
                                                placeholder="ราคาต่อหน่วย"
                                                formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={value => value.replace(/฿\s?|(,*)/g, '')}
                                                onChange={handleFormChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item label="รวม">
                                            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => {
                                                const prevItems = prevValues.items || [];
                                                const currentItems = currentValues.items || [];
                                                return JSON.stringify(prevItems) !== JSON.stringify(currentItems);
                                            }}>
                                                {({ getFieldValue }) => {
                                                    const items = getFieldValue('items') || [];
                                                    const item = items[index] || {};
                                                    const total = (item.quantity || 0) * (item.unit_price || 0);
                                                    return (
                                                        <InputNumber
                                                            value={total}
                                                            disabled
                                                            style={{ width: '100%' }}
                                                            formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                        />
                                                    );
                                                }}
                                            </Form.Item>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                        <Button
                            type="dashed"
                            onClick={() => add({ description: '', quantity: 1, unit_price: 0, unit: 'ชิ้น' })}
                            block
                            icon={<PlusOutlined />}
                        >
                            เพิ่มรายการ
                        </Button>
                    </>
                )}
            </Form.List>

            <Divider>การคำนวณภาษี</Divider>

            {/* VAT Mode Selection */}
            <Row gutter={isMobile ? [0, 16] : 16}>
                <Col xs={24} sm={24}>
                    <Form.Item
                        name="vat_mode"
                        label={
                            <span>
                                <CalculatorOutlined style={{ marginRight: 8 }} />
                                โหมดการคำนวณภาษี
                            </span>
                        }
                        rules={[{ required: true, message: 'กรุณาเลือกโหมดการคำนวณภาษี' }]}
                    >
                        <Radio.Group onChange={handleFormChange}>
                            <Radio.Button value="novat">ไม่มี VAT</Radio.Button>
                            <Radio.Button value="exclude">VAT EXCLUDE(ยังไม่รวมภาษี)</Radio.Button>
                            <Radio.Button value="include">VAT INCLUDE(รวมภาษีแล้ว)</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="tax_rate"
                        label="อัตราภาษี (%)"
                    >
                        <InputNumber
                            min={0}
                            max={100}
                            step={0.01}
                            style={{ width: '100%' }}
                            onChange={handleFormChange}
                        />
                    </Form.Item>
                </Col>
            </Row>

            {/* VAT Mode Explanation */}
            <Alert
                message={
                    vatMode === 'include'
                        ? "โหมด VAT INCLUDE: ราคาสินค้าที่ป้อนรวมภาษีแล้ว ระบบจะแยกคำนวณภาษีออกจากยอดรวม"
                        : "โหมด VAT EXCLUDE: ราคาสินค้าที่ป้อนยังไม่รวมภาษี ระบบจะคำนวณภาษีเพิ่มเติม"
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Row gutter={isMobile ? [0, 16] : 16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="discount"
                        label="ส่วนลด"
                    >
                        <InputNumber
                            min={0}
                            step={0.01}
                            style={{ width: '100%' }}
                            formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/฿\s?|(,*)/g, '')}
                            onChange={handleFormChange}
                        />
                    </Form.Item>
                </Col>
            </Row>

            {/* Calculation Summary */}

            <Card title="สรุปการคำนวณ" style={{ marginBottom: 16 }}>
                <Row>
                    <Col span={12}><strong>ยอดก่อนภาษี:</strong></Col>
                    <Col span={12} style={{ textAlign: 'right' }}>{calcValues.subtotal}</Col>
                </Row>
                {calcValues.discountAmount > 0 && (
                    <Row>
                        <Col span={12}><strong>ส่วนลด:</strong></Col>
                        <Col span={12} style={{ textAlign: 'right' }}>-{calcValues.discountAmount}</Col>
                    </Row>
                )}
                <Row>
                    <Col span={12}><strong>ภาษีมูลค่าเพิ่ม:</strong></Col>
                    <Col span={12} style={{ textAlign: 'right' }}>{calcValues.taxAmount}</Col>
                </Row>
                <Divider style={{ margin: '8px 0' }} />
                <Row>
                    <Col span={12}><strong style={{ color: '#1890ff' }}>ยอดรวมทั้งสิ้น:</strong></Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <strong style={{ color: '#1890ff' }}>{calcValues.totalAmount}</strong>
                    </Col>
                </Row>
            </Card>


            <Form.Item
                name="notes"
                label="หมายเหตุ"
            >
                <TextArea rows={3} placeholder="หมายเหตุเพิ่มเติม" />
            </Form.Item>

            {!isMobile && (
                <Form.Item>
                    <Space>
                        <Button onClick={onCancel}>
                            ยกเลิก
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            บันทึก
                        </Button>
                    </Space>
                </Form.Item>
            )}
        </Form>
    );
};

// Quote Detail Component
const QuoteDetail = ({ quote, isMobile }) => {
    // Status options and colors (local copy for this component)
    const statusOptions = [
        { value: 'draft', label: 'ร่าง', color: 'default' },
        { value: 'sent', label: 'ส่งแล้ว', color: 'processing' },
        { value: 'accepted', label: 'อนุมัติแล้ว', color: 'success' },
        { value: 'rejected', label: 'ปฏิเสธ', color: 'error' },
        { value: 'expired', label: 'หมดอายุ', color: 'warning' },
    ];

    const getStatusColor = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.color || 'default';
    };

    const getStatusLabel = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.label || status;
    };

    return (
        <Tabs defaultActiveKey="1">
            <TabPane tab="ข้อมูลใบเสนอราคา" key="1">
                <Descriptions column={isMobile ? 1 : 2} bordered size={isMobile ? "small" : "default"}>
                    <Descriptions.Item label="เลขที่ใบเสนอราคา" span={isMobile ? 1 : 2}>
                        {quote.quote_number}
                    </Descriptions.Item>
                    <Descriptions.Item label="ลูกค้า" span={isMobile ? 1 : 2}>
                        <div>
                            <div>{quote.contact_name}</div>
                            {quote.company_name && (
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    {quote.company_name}
                                </div>
                            )}
                        </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="วันที่เสนอราคา">
                        {dayjs(quote.quote_date).format('DD/MM/YYYY')}
                    </Descriptions.Item>
                    <Descriptions.Item label="วันหมดอายุ">
                        {quote.valid_until ? dayjs(quote.valid_until).format('DD/MM/YYYY') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="สถานะ" span={isMobile ? 1 : 2}>
                        <Tag color={getStatusColor(quote.status)}>
                            {getStatusLabel(quote.status)}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="โหมดการคำนวณภาษี" span={isMobile ? 1 : 2}>
                        <Tag color={quote.vat_mode === 'include' ? 'green' : 'blue'}>
                            {quote.vat_mode === 'include' ? 'VAT INCLUDE (รวมภาษีแล้ว)' : 'VAT EXCLUDE (ยังไม่รวมภาษี)'}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="ยอดรวมก่อนภาษี">
                        ฿{parseFloat(quote.subtotal).toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="ส่วนลด">
                        ฿{parseFloat(quote.discount || 0).toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label={`ภาษี (${quote.tax_rate || 0}%)`}>
                        ฿{parseFloat(quote.tax_amount).toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="ยอดรวมทั้งสิ้น">
                        <strong>฿{parseFloat(quote.total_amount).toLocaleString()}</strong>
                    </Descriptions.Item>
                    {quote.notes && (
                        <Descriptions.Item label="หมายเหตุ" span={isMobile ? 1 : 2}>
                            {quote.notes}
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label="ผู้สร้าง">
                        {quote.created_by_name}
                    </Descriptions.Item>
                    <Descriptions.Item label="วันที่สร้าง">
                        {dayjs(quote.created_at).format('DD/MM/YYYY HH:mm')}
                    </Descriptions.Item>
                </Descriptions>
            </TabPane>

            <TabPane tab="รายการสินค้า" key="2">
                {quote.items && quote.items.length > 0 ? (
                    isMobile ? (
                        /* Mobile: Card List */
                        <List
                            dataSource={quote.items}
                            renderItem={(item, index) => (
                                <Card
                                    size="small"
                                    style={{ marginBottom: 8 }}
                                    title={`รายการที่ ${index + 1}`}
                                >
                                    <p><strong>รายการ:</strong> {item.description}</p>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <p><strong>จำนวน:</strong> {item.quantity} {item.unit || 'ชิ้น'}</p>
                                        </Col>
                                        <Col span={12}>
                                            <p><strong>ราคา/หน่วย:</strong> ฿{parseFloat(item.unit_price).toLocaleString()}</p>
                                        </Col>
                                        <Col span={12}>
                                            <p><strong>รวม:</strong> ฿{parseFloat(item.total_price).toLocaleString()}</p>
                                        </Col>
                                    </Row>
                                </Card>
                            )}
                        />
                    ) : (
                        /* Desktop: Table */
                        <Table
                            dataSource={quote.items}
                            rowKey="id"
                            pagination={false}
                            columns={[
                                {
                                    title: 'รายการ',
                                    dataIndex: 'description',
                                    key: 'description',
                                },
                                {
                                    title: 'จำนวน',
                                    key: 'quantity',
                                    align: 'center',
                                    width: 120,
                                    render: (_, record) => `${record.quantity} ${record.unit || 'ชิ้น'}`,
                                },
                                {
                                    title: 'ราคาต่อหน่วย',
                                    dataIndex: 'unit_price',
                                    key: 'unit_price',
                                    align: 'right',
                                    width: 120,
                                    render: (price) => `฿${parseFloat(price).toLocaleString()}`,
                                },
                                {
                                    title: 'รวม',
                                    dataIndex: 'total_price',
                                    key: 'total_price',
                                    align: 'right',
                                    width: 120,
                                    render: (total) => `฿${parseFloat(total).toLocaleString()}`,
                                },
                            ]}
                        />
                    )
                ) : (
                    <Empty description="ไม่มีรายการสินค้า" />
                )}
            </TabPane>
        </Tabs>
    );
};

export default QuotesPage;
